import type { ReactElement } from 'react'
import { memo, useState } from 'react'
import { Check, ExternalLink, ImageOff, RefreshCw, RotateCcw, X } from 'lucide-react'
import type { ImageDTO, JobDTO } from '@shared/types'
import { ImageStatus, hasOriginalAlt, isApprovable } from '@shared/types'
import { Badge, Button, Checkbox, Spinner, Tooltip, useToast } from '@/components/ui'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { relativeTime } from '@/lib/format'
import { useJobStore } from '@/stores/useJobStore'
import { useMediaStore } from '@/stores/useMediaStore'
import { AltTextEditor } from './AltTextEditor'

export interface ImageCardProps {
  image: ImageDTO
  /** Already trimmed of a trailing slash. Empty hides the WordPress deep link. */
  siteUrl: string
  /** A job owns this site, so a one-off generation would be rejected with a 409. */
  siteBusy: boolean
  /** This row is inside the SAVE job currently writing to WordPress. */
  saving: boolean
}

function ImageCardBase({ image, siteUrl, siteBusy, saving }: ImageCardProps): ReactElement {
  const [thumbBroken, setThumbBroken] = useState(false)
  const [regenerating, setRegenerating] = useState(false)

  // Subscribed per card rather than passed down, so selecting one image
  // re-renders one card instead of the whole grid.
  const selected = useMediaStore((state) => state.selected.has(image.id))
  const toggleSelect = useMediaStore((state) => state.toggleSelect)
  const patchImage = useMediaStore((state) => state.patchImage)
  const applyJobEvent = useJobStore((state) => state.applyEvent)
  const { toast } = useToast()

  const working =
    image.status === ImageStatus.Downloading || image.status === ImageStatus.Processing
  const queued = image.status === ImageStatus.Queued
  const failed = image.status === ImageStatus.Failed || image.error !== null
  const title = image.title.trim() || `Attachment ${image.wpMediaId}`
  const kind = image.mimeType.split('/')[1] ?? ''
  const dimensions = image.width > 0 && image.height > 0 ? `${image.width}×${image.height}` : ''

  const setApproved = (approved: boolean): void => {
    void patchImage(image.id, { approved }).catch((error: unknown) => {
      toast({
        title: approved ? 'Could not approve that image' : 'Could not unapprove that image',
        description: errorMessage(error),
        variant: 'error'
      })
    })
  }

  const regenerate = async (): Promise<void> => {
    setRegenerating(true)
    try {
      // A 1-item job, not a blocking call: generation can take a minute.
      const job = await api.post<JobDTO>(`/media/${image.id}/generate`)
      applyJobEvent({ type: 'job', job })
    } catch (error) {
      toast({
        title: 'Could not start generating',
        description: errorMessage(error),
        variant: 'error'
      })
    } finally {
      setRegenerating(false)
    }
  }

  return (
    <article
      // Spelled out rather than the `card` utility: `cn` does not merge
      // conflicting classes, so the border colour has to be the only one set.
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-card border bg-surface shadow-sheet transition-shadow duration-150 ease-out-quart hover:shadow-pop',
        selected && 'border-accent/70 ring-2 ring-accent/25',
        !selected && failed && 'border-bad-500/40'
      )}
    >
      <div className="flex items-start justify-between gap-2 px-3 py-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {working ? (
            <Badge size="sm" variant="accent">
              <Spinner size="sm" />
              Generating
            </Badge>
          ) : null}
          {queued ? (
            <Badge size="sm" variant="neutral">
              Queued
            </Badge>
          ) : null}
          {hasOriginalAlt(image) ? (
            <Tooltip content="WordPress already had alt text for this image">
              <Badge size="sm" variant="warning">
                Has alt
              </Badge>
            </Tooltip>
          ) : null}
          {image.approved && !image.saved ? (
            <Badge size="sm" variant="accent">
              Approved
            </Badge>
          ) : null}
          {saving ? (
            <Badge size="sm" variant="info">
              Saving
            </Badge>
          ) : null}
          {image.saved ? (
            <Badge size="sm" variant="success">
              Saved
            </Badge>
          ) : null}
          {failed ? (
            <Badge size="sm" variant="danger">
              Failed
            </Badge>
          ) : null}
        </div>

        <Checkbox
          checked={selected}
          onChange={() => toggleSelect(image.id)}
          aria-label={`Select ${title}`}
          className="shrink-0 pt-0.5"
        />
      </div>

      <div className="relative aspect-[4/3] w-full overflow-hidden border-y border-line bg-sunken">
        {image.sourceUrl && !thumbBroken ? (
          // Decorative: the title below and the alt-text field carry the meaning,
          // and WP Engine URLs 404 often enough to need the fallback.
          <img
            src={image.sourceUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setThumbBroken(true)}
            className="size-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-fg-subtle">
            <ImageOff className="size-6" aria-hidden="true" />
            <span className="text-[11px] font-medium">Image unavailable</span>
          </div>
        )}
      </div>

      <div className="min-w-0 px-3 pt-3">
        <p className="truncate text-sm font-medium text-fg" title={title}>
          {title}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-fg-subtle">
          <span className="font-mono">#{image.wpMediaId}</span>
          {dimensions ? <span>{dimensions}</span> : null}
          {kind ? <span className="uppercase">{kind}</span> : null}
          {image.saved && image.savedAt ? <span>Saved {relativeTime(image.savedAt)}</span> : null}
        </p>
      </div>

      <div className="px-3 pt-2">
        <AltTextEditor image={image} />
        {image.error ? (
          <p className="mt-1.5 text-[11px] leading-relaxed font-medium break-words text-bad-600 dark:text-bad-300">
            {image.error}
          </p>
        ) : null}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-2 px-3 py-3">
        {image.approved ? (
          <Button size="sm" variant="secondary" iconLeft={<X />} onClick={() => setApproved(false)}>
            Unapprove
          </Button>
        ) : (
          <Button
            size="sm"
            variant="success"
            iconLeft={<Check />}
            onClick={() => setApproved(true)}
            disabled={!isApprovable(image)}
            title={isApprovable(image) ? undefined : 'Needs alt text before it can be approved'}
          >
            Approve
          </Button>
        )}

        <Button
          size="sm"
          variant="secondary"
          iconLeft={failed ? <RotateCcw /> : <RefreshCw />}
          onClick={() => void regenerate()}
          loading={regenerating}
          disabled={siteBusy || working || queued}
          title={siteBusy ? 'Another job is running for this site' : undefined}
        >
          {failed ? 'Retry' : 'Regenerate'}
        </Button>

        {siteUrl ? (
          <a
            href={`${siteUrl}/wp-admin/post.php?post=${image.wpMediaId}&action=edit`}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`Open ${title} in WordPress`}
            className="ml-auto inline-flex items-center gap-1 rounded-control px-1 py-0.5 text-xs font-medium text-accent transition-colors hover:underline"
          >
            Open in WP
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </a>
        ) : null}
      </div>
    </article>
  )
}

/**
 * Memoised on purpose: a GENERATE job pushes an SSE update per image, and
 * without this every one of them would re-render the entire page of cards.
 */
export const ImageCard = memo(ImageCardBase)
